import { ApartmentSolutionSchema, ControllerAllocationProposalSchema, InfrastructureProposalSchema, isDrawingTakeoffArtifactSourceRef, projectTakeoffForEngine, SpecDecisionProposalSchema, SpecRunSchema, SpecSnapshotSchema, StrategyProposalSchema } from "@automatry/schemas";
import { findProductByPartNo, isHardIoCapabilityEligible } from "@automatry/catalog";
import { allocateControllers, getCatalogProducts } from "./controllerAllocation.js";
import { deriveDeviceMappings } from "./deviceMapping.js";
import { deterministicHash } from "./hash.js";
import { deriveInfrastructure } from "./infrastructure.js";
import { applyScopedOverridesToSolution } from "./overrideApplication.js";
import { buildPhysicalMappings } from "./physicalMapping.js";
import { buildApartmentPointLedger, buildControllerPointLedger, sumPoints } from "./pointLedger.js";
import { INTERFACE_DEMAND_POLICY_FLAG_FAMILY_IDS, resolveControlTopologySelector, restrictPolicyFlagSetToEngineInputAllowlist } from "./controlTopologySelector.js";
import { deriveStrategyContext, ENGINE_TIMESTAMP, ENGINE_VERSION, NO_RISK, uniqueSourceRefs } from "./strategyContext.js";
import { dispatchEngineTool, ENGINE_TOOL_NAMES } from "./tools/index.js";
import { validateApartmentSolution, validateSnapshot } from "./validate.js";
import { buildZoneModel } from "./zoneModel.js";
export function runSpecification(input, deps, options = {}) {
    const preparedPolicyFlags = prepareEngineInputPolicyFlags(input);
    const injectedDecisions = options.injectedDecisions
        ? normalizeDecisionInjection(options.injectedDecisions)
        : undefined;
    const inputHash = deterministicHash({
        input: preparedPolicyFlags.deterministicInput,
        catalog: catalogHashPayload(deps.catalog),
        rules: {
            id: deps.rules.id,
            version: deps.rules.version,
            ruleCards: deps.rules.ruleCards.map((card) => ({
                id: card.id,
                status: card.status,
                updatedAt: card.updatedAt
            })),
            loadProfiles: deps.rules.loadProfiles.map((profile) => ({
                key: profile.key,
                pointDemand: profile.pointDemand
            }))
        }
    });
    warnForExcludedPolicyFlagFamilies(input, preparedPolicyFlags.excludedFamilyIds);
    const snapshotId = `snapshot-${inputHash.slice(0, 16)}`;
    const runId = `run-${inputHash.slice(0, 16)}`;
    const createdByJobId = `job-${inputHash.slice(0, 16)}`;
    let previousSolutionHash = "";
    let snapshot = buildSnapshot(preparedPolicyFlags.deterministicInput, deps, {
        inputHash,
        snapshotId,
        createdByJobId,
        previousValidation: undefined,
        ...(injectedDecisions ? { injectedDecisions } : {})
    });
    for (let iteration = 0; iteration < 3; iteration += 1) {
        const solutionHash = deterministicHash(snapshot);
        if (solutionHash === previousSolutionHash) {
            break;
        }
        previousSolutionHash = solutionHash;
        const validation = validateSnapshot(snapshot, deps.catalog);
        snapshot = {
            ...snapshot,
            status: validation.status === "rejected" ? "rejected" : "accepted",
            validation,
            apartmentSolutions: snapshot.apartmentSolutions.map((solution) => ({
                ...solution,
                validation: solution.validation.status === "rejected"
                    ? solution.validation
                    : validateApartmentSolution({
                        id: `validation-${solution.apartmentTypeId}`,
                        solution,
                        catalog: deps.catalog,
                        sourceRefs: solution.sourceRefs,
                        ruleTraceRefs: solution.ruleTraceRefs
                    })
            }))
        };
    }
    const verification = injectedDecisions
        ? verifyInjectedSnapshot(snapshot, deps, injectedDecisions)
        : undefined;
    if (verification) {
        snapshot = applyVerifierResultsToSnapshot(snapshot, verification.verifierResults);
    }
    const solutionHash = deterministicHash(snapshot);
    const parsedSnapshot = SpecSnapshotSchema.parse(snapshot);
    const run = {
        id: runId,
        orgId: deps.catalog.orgId,
        projectId: input.projectId,
        version: ENGINE_VERSION,
        catalogReleaseId: deps.catalog.id,
        ruleSetId: deps.rules.id,
        status: snapshot.validation.status === "rejected" ? "failed" : "accepted",
        inputHash,
        outputSnapshotId: snapshot.id,
        validation: snapshot.validation,
        snapshot: parsedSnapshot,
        solutionHash,
        startedAt: ENGINE_TIMESTAMP,
        completedAt: ENGINE_TIMESTAMP,
        createdByJobId
    };
    const parsedRun = SpecRunSchema.parse(run);
    return verification
        ? {
            ...parsedRun,
            verifierResults: verification.verifierResults,
            toolCallTraces: verification.toolCallTraces
        }
        : parsedRun;
}
export function getSpecSnapshot(run) {
    return run.snapshot;
}
export function getSolutionHash(run) {
    return run.solutionHash;
}
function normalizeDecisionInjection(injected) {
    const explicit = "proposal" in injected
        ? injected
        : { proposal: injected };
    const proposal = SpecDecisionProposalSchema.parse(explicit.proposal);
    const normalized = { proposal };
    const strategyProposal = explicit.strategyProposal ?? proposal.strategyProposal;
    const controllerAllocationProposal = explicit.controllerAllocationProposal ?? proposal.controllerAllocationProposal;
    const infrastructureProposal = explicit.infrastructureProposal ?? proposal.infrastructureProposal;
    if (strategyProposal) {
        normalized.strategyProposal = StrategyProposalSchema.parse(strategyProposal);
    }
    if (controllerAllocationProposal) {
        normalized.controllerAllocationProposal = ControllerAllocationProposalSchema.parse(controllerAllocationProposal);
    }
    if (infrastructureProposal) {
        normalized.infrastructureProposal =
            InfrastructureProposalSchema.parse(infrastructureProposal);
    }
    if (explicit.controllers) {
        normalized.controllers = explicit.controllers;
    }
    if (explicit.infrastructure) {
        normalized.infrastructure = explicit.infrastructure;
    }
    if (explicit.edgeFeatures) {
        normalized.edgeFeatures = explicit.edgeFeatures;
    }
    if (explicit.risks) {
        normalized.risks = explicit.risks;
    }
    return normalized;
}
function applyInjectedStrategyProposal(strategy, proposal) {
    if (!proposal) {
        return strategy;
    }
    const screenKinds = proposal.uiScreenTopology.screenKinds.map((kind) => kind.toLowerCase());
    const centralScreenRequired = proposal.uiScreenTopology.uiStrategy !== "per_room" ||
        screenKinds.some((kind) => ["central", "ccs", "ts-7", "ts7"].some((needle) => kind.includes(needle)));
    return {
        ...strategy,
        context: {
            ...strategy.context,
            ...(proposal.strategyContextId ? { id: proposal.strategyContextId } : {})
        },
        triggers: {
            ...strategy.triggers,
            uiStrategy: proposal.uiScreenTopology.uiStrategy,
            centralScreenRequired
        }
    };
}
function prepareEngineInputPolicyFlags(input) {
    const filtered = restrictPolicyFlagSetToEngineInputAllowlist(input.policyFlagSet);
    const materialFlagSet = canonicalEnginePolicyFlagSet(filtered.flagSet);
    const { policyFlagSet: _policyFlagSet, ...withoutPolicyFlags } = input;
    return {
        deterministicInput: materialFlagSet
            ? { ...input, policyFlagSet: materialFlagSet }
            : withoutPolicyFlags,
        excludedFamilyIds: filtered.excludedFamilyIds
    };
}
function canonicalEnginePolicyFlagSet(flagSet) {
    if (!flagSet || flagSet.activeFlags.length === 0) {
        return undefined;
    }
    return {
        activeFlags: flagSet.activeFlags
            .map((flag) => isInterfaceDemandPolicyFamily(flag.familyId)
            ? canonicalInterfaceDemandPolicyFlag(flag)
            : flag)
            .sort(comparePolicyFlagsForEngine)
    };
}
function canonicalInterfaceDemandPolicyFlag(flag) {
    return {
        ...flag,
        id: [
            "flag-interface-demand-material",
            flag.familyId.replaceAll(".", "-"),
            policyFlagScopeMaterialKey(flag.scope),
            policyFlagValueMaterialKey(flag.value),
            flag.provenance ?? "unspecified",
            flag.status ?? "active"
        ].join("-"),
        why: "Canonical interface-demand material vector; source flag identity and evidence refs are audit-only.",
        evidenceRefs: []
    };
}
function comparePolicyFlagsForEngine(left, right) {
    return policyFlagMaterialSortKey(left).localeCompare(policyFlagMaterialSortKey(right));
}
function policyFlagMaterialSortKey(flag) {
    return [
        flag.familyId,
        policyFlagScopeMaterialKey(flag.scope),
        policyFlagValueMaterialKey(flag.value),
        flag.provenance ?? "",
        flag.status ?? "",
        flag.id
    ].join("\0");
}
function policyFlagScopeMaterialKey(scope) {
    if (!scope) {
        return "scope-global";
    }
    return [
        `scope-${scope.level}`,
        `project-${scope.projectId}`,
        "apartmentTypeId" in scope && scope.apartmentTypeId
            ? `apartment-${scope.apartmentTypeId}`
            : "apartment-all",
        "roomRef" in scope && scope.roomRef ? `room-${scope.roomRef}` : "room-all",
        "itemClass" in scope && scope.itemClass ? `item-${scope.itemClass}` : "item-all",
        "requirementId" in scope && scope.requirementId
            ? `requirement-${scope.requirementId}`
            : "requirement-all"
    ].join("-");
}
function policyFlagValueMaterialKey(value) {
    if (value.kind === "boolean") {
        return `boolean-${String(value.value)}`;
    }
    if (value.kind === "mode" && typeof value.mode === "string") {
        return `mode-${value.mode}`;
    }
    return `kind-${value.kind}`;
}
function warnForExcludedPolicyFlagFamilies(input, excludedFamilyIds) {
    if (excludedFamilyIds.length === 0) {
        return;
    }
    // F2 Lane-B wall plus A4 carve-out: only the F1-sanctioned
    // `control-topology.central-screen-mode` family and the five A4
    // `interface-demand.*` families may reach verification-engine consumers.
    // Other reviewed F2 families reach the AI spec via the provider block built
    // from the unfiltered set upstream.
    console.warn(`[spec-engine] F2 Lane-B wall: excluded famil${excludedFamilyIds.length === 1 ? "y" : "ies"} [${excludedFamilyIds.join(", ")}] from verification-engine input for ${input.projectId}/${input.apartmentTypeId}. Only control-topology.central-screen-mode and interface-demand.* may drive verification-engine consumers; excluded families still reach the AI spec via the provider block.`);
}
function buildControllerAllocation(input) {
    if (input.injectedDecisions?.controllers) {
        return materializeInjectedControllers({
            projectId: input.projectId,
            zoneModel: input.zoneModel,
            catalog: input.catalog,
            controllers: input.injectedDecisions.controllers
        });
    }
    if (input.injectedDecisions?.controllerAllocationProposal) {
        return materializeControllerAllocationProposal({
            projectId: input.projectId,
            zoneModel: input.zoneModel,
            catalog: input.catalog,
            proposal: input.injectedDecisions.controllerAllocationProposal
        });
    }
    return allocateControllers(input.projectId, input.zoneModel, input.catalog, input.sourceRequirementIds);
}
function materializeInjectedControllers(input) {
    const catalogProducts = getCatalogProducts(input.catalog);
    const controllers = input.controllers.map((controller) => {
        const product = findProductByPartNo(catalogProducts, controller.productPartNo);
        const sourceRefs = controller.sourceRefs.length > 0
            ? controller.sourceRefs
            : input.zoneModel.sourceRefs;
        const ruleTraceRefs = controller.ruleTraceRefs.length > 0
            ? controller.ruleTraceRefs
            : input.zoneModel.ruleTraceRefs;
        const pointLedger = buildControllerPointLedger({
            controllerId: controller.id,
            pointDemands: input.zoneModel.pointDemands,
            zoneSlots: cleanZoneSlots(controller.zoneSlots),
            capacity: controller.capacity,
            sourceRefs,
            ruleTraceRefs
        });
        return {
            ...controller,
            sourceRefs,
            ruleTraceRefs,
            pointLedger,
            status: product && isHardIoCapabilityEligible(product)
                ? controller.status
                : "needs_review"
        };
    });
    return {
        controllers,
        catalogProducts,
        ruleTraceRefs: uniqueStringList(controllers.flatMap((controller) => controller.ruleTraceRefs))
    };
}
function materializeControllerAllocationProposal(input) {
    const catalogProducts = getCatalogProducts(input.catalog);
    const controllers = input.proposal.allocations.map((allocation, index) => {
        const productPartNo = allocation.proposedProductPartNo ?? `unresolved-${allocation.id}`;
        const product = findProductByPartNo(catalogProducts, productPartNo);
        const sourceRefs = uniqueSourceRefs([
            ...input.zoneModel.sourceRefs,
            ...(product?.sourceRefs ?? []),
            ...allocation.rationale.sourceRefs
        ]);
        const ruleTraceRefs = uniqueStringList([
            ...input.zoneModel.ruleTraceRefs,
            ...allocation.rationale.ruleTraceRefs
        ]);
        const zoneSlots = allocation.zoneSlots ??
            allocation.logicalZoneIds.map((logicalZoneId, slotIndex) => ({
                slot: slotIndex + 1,
                logicalZoneId,
                role: slotIndex === 0 ? "primary" : "secondary"
            }));
        const capacity = product?.capabilities.points ?? allocation.expectedPointDemand ?? zeroPoints();
        const pointLedger = buildControllerPointLedger({
            controllerId: allocation.id,
            pointDemands: input.zoneModel.pointDemands,
            zoneSlots: cleanZoneSlots(zoneSlots),
            capacity,
            sourceRefs,
            ruleTraceRefs
        });
        const productEligible = product ? isHardIoCapabilityEligible(product) : false;
        return {
            id: allocation.id || `controller-injected-${index + 1}`,
            productPartNo,
            role: parseControllerRole(allocation.proposedRole),
            physicalLocation: productPartNo === "MZ-503" ? "central_panel" : "fcu_mounted",
            zoneSlots: cleanZoneSlots(zoneSlots),
            pointLedger,
            capacity,
            sourceRequirementIds: allocation.rationale.requirementIds,
            status: productEligible ? "accepted" : "needs_review",
            why: allocation.rationale.why,
            how: "Materialized from an injected ControllerAllocationProposal and checked against catalog capacity.",
            sourceRefs,
            ruleTraceRefs,
            risk: productEligible
                ? NO_RISK
                : {
                    level: "blocker",
                    riskRefs: [`risk-${input.projectId}-${allocation.id}-catalog-gap`],
                    notes: "Injected hard-IO product is missing from the catalog products or is not reviewed."
                }
        };
    });
    return {
        controllers,
        catalogProducts,
        ruleTraceRefs: uniqueStringList(controllers.flatMap((controller) => controller.ruleTraceRefs))
    };
}
function buildInfrastructure(input) {
    if (input.injectedDecisions?.infrastructure) {
        const edgeFeatures = input.injectedDecisions.edgeFeatures ?? [];
        const infrastructure = input.injectedDecisions.infrastructure;
        return {
            edgeFeatures,
            infrastructure,
            risks: [],
            ruleTraceRefs: uniqueStringList([
                ...edgeFeatures.flatMap((feature) => feature.ruleTraceRefs),
                ...infrastructure.flatMap((item) => item.ruleTraceRefs)
            ])
        };
    }
    if (input.injectedDecisions?.infrastructureProposal) {
        return materializeInfrastructureProposal({
            projectId: input.projectId,
            apartmentTypeId: input.apartmentTypeId,
            catalog: input.catalog,
            proposal: input.injectedDecisions.infrastructureProposal
        });
    }
    return deriveInfrastructure({
        projectId: input.projectId,
        apartmentTypeId: input.apartmentTypeId,
        triggers: input.strategy.triggers,
        loadRelayOutputCounts: countCentralLoadRelayOutputs(input.zoneModel, input.allocation.controllers),
        mzControllerCount: input.allocation.controllers.filter((controller) => controller.productPartNo === "MZ-503").length,
        catalog: input.catalog,
        sourceRefs: uniqueSourceRefs([
            ...input.zoneModel.sourceRefs,
            ...input.strategy.sourceRefs
        ])
    });
}
function materializeInfrastructureProposal(input) {
    const catalogProducts = getCatalogProducts(input.catalog);
    const infrastructure = input.proposal.items.map((item) => {
        const product = item.proposedProductPartNo
            ? findProductByPartNo(catalogProducts, item.proposedProductPartNo)
            : undefined;
        const sourceRefs = uniqueSourceRefs([
            ...item.rationale.sourceRefs,
            ...(product?.sourceRefs ?? [])
        ]);
        const ruleTraceRefs = item.rationale.ruleTraceRefs;
        return {
            id: item.id,
            itemKind: coerceInfrastructureItemKind(item.itemKind),
            supplySource: product?.supplySource ?? "third_party",
            installIncluded: item.installIncluded ?? true,
            ...(item.installIncludedBasis
                ? { installIncludedBasis: item.installIncludedBasis }
                : {}),
            quantity: item.quantity,
            ...(item.proposedProductPartNo
                ? { productPartNo: item.proposedProductPartNo }
                : {}),
            ...(item.protocols ? { protocols: item.protocols } : {}),
            ...(item.connectivity ? { connectivity: item.connectivity } : {}),
            ...(item.provisionScope ? { provisionScope: item.provisionScope } : {}),
            ...(item.provisionScopeBasis
                ? { provisionScopeBasis: item.provisionScopeBasis }
                : {}),
            selectionStatus: product ? "selected" : "placeholder",
            ...(product
                ? { sourceStatus: product.sourceStatus }
                : { sourceStatus: "research-needed" }),
            why: item.rationale.why,
            how: "Materialized from an injected InfrastructureProposal and checked against the supplied catalog.",
            sourceRefs,
            ruleTraceRefs,
            risk: product || !item.proposedProductPartNo
                ? NO_RISK
                : {
                    level: "blocker",
                    riskRefs: [`risk-${input.apartmentTypeId}-${item.id}-catalog-gap`],
                    notes: "Injected infrastructure product is absent from the supplied catalog."
                }
        };
    });
    return {
        edgeFeatures: [],
        infrastructure,
        risks: [],
        ruleTraceRefs: uniqueStringList(infrastructure.flatMap((item) => item.ruleTraceRefs))
    };
}
function verifyInjectedSnapshot(snapshot, deps, injectedDecisions) {
    const solution = snapshot.apartmentSolutions[0];
    if (!solution) {
        return {
            verifierResults: [],
            toolCallTraces: []
        };
    }
    const dispatches = [
        dispatchEngineTool(makeToolRequest({
            id: `tool-request-${snapshot.id}-point-capacity`,
            toolName: ENGINE_TOOL_NAMES.pointCapacity,
            projectId: snapshot.projectId,
            input: {
                proposalId: injectedDecisions.proposal.id,
                proposalKind: "controller_allocation",
                apartmentTypeId: solution.apartmentTypeId,
                pointDemands: solution.pointDemands,
                controllers: solution.controllers.map((controller) => ({
                    id: controller.id,
                    productPartNo: controller.productPartNo,
                    capacity: controller.capacity,
                    zoneSlots: cleanZoneSlots(controller.zoneSlots),
                    sourceRefs: controller.sourceRefs,
                    ruleTraceRefs: controller.ruleTraceRefs
                })),
                sourceRefs: solution.sourceRefs,
                ruleTraceRefs: solution.ruleTraceRefs
            }
        }), deps),
        dispatchEngineTool(makeToolRequest({
            id: `tool-request-${snapshot.id}-snapshot-validation`,
            toolName: ENGINE_TOOL_NAMES.snapshotValidation,
            projectId: snapshot.projectId,
            input: {
                proposalId: injectedDecisions.proposal.id,
                proposalKind: injectedDecisions.proposal.proposalKind,
                snapshot
            }
        }), deps)
    ];
    const ruleInvariantControllers = toRuleInvariantControllers(solution);
    if (ruleInvariantControllers.length > 0) {
        dispatches.push(dispatchEngineTool(makeToolRequest({
            id: `tool-request-${snapshot.id}-rule-invariants`,
            toolName: ENGINE_TOOL_NAMES.ruleInvariants,
            projectId: snapshot.projectId,
            input: {
                proposalId: injectedDecisions.proposal.id,
                proposalKind: "controller_allocation",
                controllers: ruleInvariantControllers,
                sourceRefs: solution.sourceRefs,
                ruleTraceRefs: solution.ruleTraceRefs
            }
        }), deps));
    }
    return {
        verifierResults: dispatches.flatMap((dispatch) => dispatch.verifierResult ? [dispatch.verifierResult] : []),
        toolCallTraces: dispatches.map((dispatch) => dispatch.trace)
    };
}
function applyVerifierResultsToSnapshot(snapshot, verifierResults) {
    const blockingFindings = verifierResults.flatMap((result) => result.status === "violation"
        ? result.findings.filter((finding) => ["error", "blocker"].includes(finding.severity))
        : []);
    if (blockingFindings.length === 0) {
        return snapshot;
    }
    const validation = {
        ...snapshot.validation,
        status: "rejected",
        summary: "Validation rejected injected decisions after verifier checks.",
        findings: [
            ...snapshot.validation.findings,
            ...blockingFindings.map(validationFindingFromVerifierFinding)
        ],
        checkedAt: ENGINE_TIMESTAMP
    };
    return SpecSnapshotSchema.parse({
        ...snapshot,
        status: "rejected",
        validation
    });
}
function validationFindingFromVerifierFinding(finding) {
    return {
        code: `verifier.${finding.code}`,
        message: finding.message,
        severity: finding.severity === "warning" || finding.severity === "info"
            ? finding.severity
            : "error",
        ...(finding.subjectRef ? { subjectRef: finding.subjectRef } : {}),
        sourceRefs: finding.sourceRefs,
        ruleTraceRefs: finding.ruleTraceRefs
    };
}
function makeToolRequest(input) {
    return {
        id: input.id,
        toolName: input.toolName,
        projectId: input.projectId,
        input: input.input,
        idempotencyKey: deterministicHash(input),
        requestedAt: ENGINE_TIMESTAMP
    };
}
function toRuleInvariantControllers(solution) {
    const zonesById = new Map(solution.logicalZones.map((zone) => [
        zone.id,
        zone
    ]));
    return solution.controllers.flatMap((controller) => {
        if (!isRuleControllerPartNo(controller.productPartNo) ||
            !isRuleControllerRole(controller.role) ||
            !isRuleControllerLocation(controller.physicalLocation)) {
            return [];
        }
        return [
            {
                id: controller.id,
                productPartNo: controller.productPartNo,
                role: controller.role,
                physicalLocation: controller.physicalLocation,
                zoneSlots: controller.zoneSlots.flatMap((slot) => {
                    const zone = zonesById.get(slot.logicalZoneId);
                    if (!zone || !isRuleZoneKind(zone.zoneKind)) {
                        return [];
                    }
                    return [
                        {
                            slot: slot.slot,
                            role: slot.role,
                            logicalZoneId: slot.logicalZoneId,
                            zoneKind: zone.zoneKind,
                            combinedLoadZoneIds: extractCombinedZoneIds(slot.notes),
                            ...(slot.notes ? { notes: slot.notes } : {})
                        }
                    ];
                })
            }
        ];
    });
}
function parseControllerRole(value) {
    const roles = [
        "fcu_primary",
        "heat_only_710",
        "mz_overflow"
    ];
    if (roles.includes(value)) {
        return value;
    }
    throw new Error(`Invalid controller allocation proposedRole "${value}"; expected fcu_primary, heat_only_710, or mz_overflow.`);
}
function coerceInfrastructureItemKind(value) {
    const itemKinds = [
        "netmaster",
        "panel",
        "enclosure",
        "power_supply",
        "switch",
        "remote_io_panel",
        "gateway",
        "edge_compute",
        "network_switch",
        "edge_enclosure",
        "cloud_service",
        "metering_unit",
        "relay",
        "io_module",
        "cabling_note",
        "other"
    ];
    return itemKinds.includes(value)
        ? value
        : "other";
}
function isRuleControllerPartNo(value) {
    return value === "TP-C-710R-IP" || value === "MZ-503";
}
function isRuleControllerRole(value) {
    return ["fcu_primary", "heat_only_710", "mz_overflow"].includes(value);
}
function isRuleControllerLocation(value) {
    return value === "fcu_mounted" || value === "central_panel";
}
function isRuleZoneKind(value) {
    return ["fcu", "wufh", "eufh", "heat_only"].includes(value);
}
function zeroPoints() {
    return {
        ui: 0,
        di: 0,
        ao: 0,
        do: 0,
        relay: 0
    };
}
function uniqueStringList(values) {
    return [...new Set(values)];
}
function cleanZoneSlots(zoneSlots) {
    return zoneSlots.map((slot) => ({
        slot: slot.slot,
        logicalZoneId: slot.logicalZoneId,
        role: slot.role,
        ...(slot.notes ? { notes: slot.notes } : {})
    }));
}
function buildSnapshot(input, deps, runContext) {
    const engineSafeTakeoffRows = input.takeoffRows.map(projectTakeoffForEngine);
    const strategy = applyInjectedStrategyProposal(deriveStrategyContext(input.projectId, input.apartmentTypeId, engineSafeTakeoffRows, input.requirements, deps.catalog.id, deps.rules, input.takeoffRows
        .flatMap((row) => row.sourceRefs)
        .filter((sourceRef) => !isDrawingTakeoffArtifactSourceRef(sourceRef))), runContext.injectedDecisions?.strategyProposal);
    const zoneModel = buildZoneModel(input.projectId, input.apartmentTypeId, input.takeoffRows, deps.rules);
    const sourceRequirementIds = input.requirements.map((requirement) => requirement.id);
    const controlTopologySelector = resolveControlTopologySelector({
        projectId: input.projectId,
        apartmentTypeId: input.apartmentTypeId,
        ...(input.policyFlagSet ? { policyFlagSet: input.policyFlagSet } : {})
    });
    const allocation = buildControllerAllocation({
        projectId: input.projectId,
        zoneModel,
        catalog: deps.catalog,
        sourceRequirementIds,
        ...(runContext.injectedDecisions
            ? { injectedDecisions: runContext.injectedDecisions }
            : {})
    });
    const infrastructure = buildInfrastructure({
        projectId: input.projectId,
        apartmentTypeId: input.apartmentTypeId,
        strategy,
        zoneModel,
        allocation,
        catalog: deps.catalog,
        ...(runContext.injectedDecisions
            ? { injectedDecisions: runContext.injectedDecisions }
            : {})
    });
    const mappings = buildPhysicalMappings({
        apartmentTypeId: input.apartmentTypeId,
        zoneModel,
        controllers: allocation.controllers,
        sourceRefs: zoneModel.sourceRefs
    });
    const deviceDerivation = deriveDeviceMappings({
        projectId: input.projectId,
        apartmentTypeId: input.apartmentTypeId,
        takeoffRows: input.takeoffRows,
        zoneModel,
        controllers: allocation.controllers,
        uiStrategy: strategy.triggers.uiStrategy,
        centralScreenRequired: strategy.triggers.centralScreenRequired,
        meteringTopology: strategy.triggers.meteringTopology,
        meteringMeterProducts: strategy.triggers.meteringMeterProducts,
        sourceProductQuantities: getSourceProductQuantities(input.requirements),
        sourceRefs: zoneModel.sourceRefs,
        ...(input.policyFlagSet ? { policyFlagSet: input.policyFlagSet } : {}),
        ...(controlTopologySelector ? { controlTopologySelector } : {})
    });
    const ruleTraceRefs = [
        ...new Set([
            ...strategy.ruleTraceRefs,
            ...zoneModel.ruleTraceRefs,
            ...allocation.ruleTraceRefs,
            ...infrastructure.ruleTraceRefs,
            ...mappings.ruleTraceRefs,
            ...deviceDerivation.ruleTraceRefs
        ])
    ];
    const sourceRefs = uniqueSourceRefs([
        ...strategy.sourceRefs,
        ...zoneModel.sourceRefs,
        ...allocation.controllers.flatMap((controller) => controller.sourceRefs),
        ...(controlTopologySelector?.evidenceRefs ?? [])
    ]);
    const appliedPolicyFlagRefs = activePolicyFlagRefsForApartment(input.policyFlagSet, input.projectId, input.apartmentTypeId);
    const solutionRisks = runContext.injectedDecisions?.risks ?? [
        ...strategy.risks,
        ...infrastructure.risks,
        ...deviceDerivation.risks,
        ...makeControllerPointLedgerSuggestionRisks({
            projectId: input.projectId,
            controllers: allocation.controllers
        })
    ];
    const pointLedger = buildApartmentPointLedger({
        apartmentTypeId: input.apartmentTypeId,
        pointDemands: zoneModel.pointDemands,
        controllers: allocation.controllers,
        sourceRefs,
        ruleTraceRefs
    });
    const baseSolutionWithoutValidation = {
        apartmentTypeId: input.apartmentTypeId,
        quantity: zoneModel.quantity,
        strategyContextId: strategy.context.id,
        logicalZones: zoneModel.logicalZones,
        thermalLoads: zoneModel.thermalLoads,
        pointDemands: zoneModel.pointDemands,
        controllers: allocation.controllers,
        devices: deviceDerivation.devices,
        ...(infrastructure.edgeFeatures.length > 0
            ? { edgeFeatures: infrastructure.edgeFeatures }
            : {}),
        infrastructure: infrastructure.infrastructure,
        physicalMappings: mappings.physicalMappings,
        mappings: [...mappings.deviceMappings, ...deviceDerivation.deviceMappings],
        pointLedger,
        risks: solutionRisks,
        justifications: [
            makeJustification({
                id: `justification-${input.apartmentTypeId}-controller-plan`,
                targetRef: input.apartmentTypeId,
                sourceRefs,
                ruleTraceRefs
            })
        ],
        ...(appliedPolicyFlagRefs.length > 0 ? { appliedPolicyFlagRefs } : {}),
        why: "Apartment solution is derived from structured takeoff rows and approved requirements.",
        how: "Lane B built zones, loaded rule profiles, allocated controllers, accounted hard IO, mapped physical terminations, and derived conditional infrastructure.",
        sourceRefs,
        ruleTraceRefs,
        risk: solutionRisks.length === 0
            ? NO_RISK
            : {
                level: solutionRisks.some((risk) => risk.severity === "blocker")
                    ? "blocker"
                    : "warning",
                riskRefs: solutionRisks.map((risk) => risk.id),
                notes: "Solution carries explicit non-destructive risks for unresolved choices."
            }
    };
    const overrideApplication = applyScopedOverridesToSolution({
        projectId: input.projectId,
        apartmentTypeId: input.apartmentTypeId,
        solution: baseSolutionWithoutValidation,
        overrides: input.overrides,
        catalog: deps.catalog
    });
    const solutionWithoutValidation = overrideApplication.solution;
    const solutionValidation = validateApartmentSolution({
        id: `validation-${input.apartmentTypeId}`,
        solution: solutionWithoutValidation,
        catalog: deps.catalog,
        sourceRefs,
        ruleTraceRefs
    });
    const solution = ApartmentSolutionSchema.parse({
        ...solutionWithoutValidation,
        validation: solutionValidation
    });
    const placeholderValidation = runContext.previousValidation ?? {
        id: `validation-${runContext.snapshotId}`,
        status: solutionValidation.status,
        summary: "Snapshot validation pending fixed-point pass.",
        findings: solutionValidation.findings,
        checkedAt: ENGINE_TIMESTAMP,
        sourceRefs,
        ruleTraceRefs
    };
    const snapshot = {
        id: runContext.snapshotId,
        orgId: deps.catalog.orgId,
        projectId: input.projectId,
        version: ENGINE_VERSION,
        catalogReleaseId: deps.catalog.id,
        ruleSetId: deps.rules.id,
        engineVersion: ENGINE_VERSION,
        status: solutionValidation.status === "rejected" ? "rejected" : "accepted",
        apartmentSolutions: [solution],
        totals: buildTotals([solution]),
        validation: placeholderValidation,
        sourceRequirementIds,
        sourceOverrideIds: overrideApplication.appliedOverrideIds,
        createdAt: ENGINE_TIMESTAMP,
        createdByJobId: runContext.createdByJobId
    };
    const finalValidation = validateSnapshot(snapshot, deps.catalog);
    return SpecSnapshotSchema.parse({
        ...snapshot,
        status: finalValidation.status === "rejected" ? "rejected" : "accepted",
        validation: finalValidation
    });
}
function countCentralLoadRelayOutputs(zoneModel, controllers) {
    const zonesById = new Map(zoneModel.logicalZones.map((zone) => [zone.id, zone]));
    let individualHighLoadOutputCount = zoneModel.towelRailOutputCount + zoneModel.sourceEnableOutputCount;
    let groupedWufhOutputCount = 0;
    for (const controller of controllers) {
        const is710 = controller.productPartNo === "TP-C-710R-IP";
        for (const slot of controller.zoneSlots) {
            const zone = zonesById.get(slot.logicalZoneId);
            if (zone?.zoneKind === "eufh") {
                individualHighLoadOutputCount += 1;
            }
            if (is710 && zone?.zoneKind === "wufh") {
                groupedWufhOutputCount += 1;
            }
            for (const combinedZoneId of extractCombinedZoneIds(slot.notes)) {
                const combinedZone = zonesById.get(combinedZoneId);
                if (is710 && combinedZone?.zoneKind === "wufh") {
                    groupedWufhOutputCount += 1;
                }
            }
        }
    }
    return {
        individualHighLoadOutputCount,
        groupedWufhOutputCount
    };
}
function extractCombinedZoneIds(notes) {
    if (!notes?.includes("Combined load zones:")) {
        return [];
    }
    const [, combinedText] = notes.split("Combined load zones:");
    if (!combinedText) {
        return [];
    }
    return combinedText
        .replace(".", "")
        .split(",")
        .map((zoneId) => zoneId.trim())
        .filter(Boolean);
}
function makeControllerPointLedgerSuggestionRisks(input) {
    return input.controllers.flatMap((controller) => (controller.pointLedger.suggestions ?? []).map((suggestion) => ({
        id: suggestion.riskRef,
        projectId: input.projectId,
        type: "needsUserDecision",
        severity: suggestion.severity,
        scope: {
            level: "controller",
            refId: controller.id
        },
        title: "Spare controller relay could be used as a DO",
        plainEnglish: suggestion.message,
        technicalTrace: suggestion.sourceRefs,
        recommendedActions: [
            {
                action: suggestion.recommendedAction,
                owner: "estimator",
                status: "open"
            }
        ],
        status: "open",
        blocksExport: false,
        sourceRefs: suggestion.sourceRefs,
        ruleTraceRefs: suggestion.ruleTraceRefs,
        createdAt: ENGINE_TIMESTAMP
    })));
}
function buildTotals(solutions) {
    const productQuantities = {};
    const pointTotals = sumPoints(solutions.map((solution) => ({
        ui: solution.pointLedger.required.ui * solution.quantity,
        di: solution.pointLedger.required.di * solution.quantity,
        ao: solution.pointLedger.required.ao * solution.quantity,
        do: solution.pointLedger.required.do * solution.quantity,
        relay: solution.pointLedger.required.relay * solution.quantity
    })));
    let apartmentCount = 0;
    let riskCount = 0;
    for (const solution of solutions) {
        apartmentCount += solution.quantity;
        riskCount += solution.risks.length;
        for (const controller of solution.controllers) {
            productQuantities[controller.productPartNo] =
                (productQuantities[controller.productPartNo] ?? 0) + solution.quantity;
        }
        for (const device of solution.devices) {
            const multiplier = device.provisionScope === "project" ? 1 : solution.quantity;
            productQuantities[device.productPartNo] =
                (productQuantities[device.productPartNo] ?? 0) + device.quantity * multiplier;
        }
        for (const item of solution.infrastructure) {
            if (item.selectionStatus === "covered_by_existing") {
                continue;
            }
            const key = item.productPartNo ?? item.itemKind;
            const multiplier = item.provisionScope === "project" ? 1 : solution.quantity;
            productQuantities[key] =
                (productQuantities[key] ?? 0) + item.quantity * multiplier;
        }
    }
    return {
        apartmentCount,
        productQuantities,
        pointTotals,
        riskCount
    };
}
function getSourceProductQuantities(requirements) {
    const result = [];
    for (const requirement of requirements) {
        if (requirement.status !== "approved" ||
            requirement.productQuantity === undefined) {
            continue;
        }
        result.push({
            ...requirement.productQuantity,
            requirementId: requirement.id,
            sourceRefs: requirement.sourceRefs
        });
    }
    return result;
}
function activePolicyFlagRefsForApartment(policyFlagSet, projectId, apartmentTypeId) {
    const refs = policyFlagSet?.activeFlags.flatMap((flag) => {
        if (flag.status === "superseded") {
            return [];
        }
        if (isInterfaceDemandPolicyFamily(flag.familyId)) {
            return [];
        }
        const scope = flag.scope;
        if (!scope) {
            return [flag.id];
        }
        if (scope.projectId !== projectId) {
            return [];
        }
        if (scope.level === "apartment-type" || scope.level === "room") {
            return scope.apartmentTypeId === apartmentTypeId ? [flag.id] : [];
        }
        if (scope.level === "item-class" && scope.apartmentTypeId !== undefined) {
            return scope.apartmentTypeId === apartmentTypeId ? [flag.id] : [];
        }
        return [flag.id];
    }) ?? [];
    return [...new Set(refs)];
}
function isInterfaceDemandPolicyFamily(familyId) {
    return INTERFACE_DEMAND_POLICY_FLAG_FAMILY_IDS.includes(familyId);
}
function makeJustification(input) {
    return {
        id: input.id,
        targetRef: input.targetRef,
        why: "The selected controller plan satisfies the pinned Lane B controller-packing rules for this takeoff.",
        how: "The engine used one 710R per FCU, packed available secondary slots before MZ, and checked point ledgers against catalog capacity.",
        assumptions: [
            "Structured takeoff counts are approved for this apartment type.",
            "Same-room WUFH zones are paired to FCU zones in takeoff order when no richer room adjacency is supplied.",
            "Supply-air (off-coil) TPTVAV/S sensor fitted as standard, one per modelled FCU zone, for a higher-quality install and improved Edge anomaly detection; confirm or remove if not required."
        ],
        sourceRefs: input.sourceRefs,
        ruleTraceRefs: input.ruleTraceRefs,
        createdAt: ENGINE_TIMESTAMP
    };
}
function catalogHashPayload(catalog) {
    const candidate = catalog;
    return {
        id: catalog.id,
        orgId: catalog.orgId,
        version: catalog.version,
        status: catalog.status,
        products: candidate.products?.map((product) => ({
            partNo: product.partNo,
            capabilityStatus: product.capabilityStatus,
            capabilities: product.capabilities
        })) ?? []
    };
}
//# sourceMappingURL=runSpecification.js.map